import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Badge,
  Avatar,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Gavel as GavelIcon,
  InfoOutlined as AboutIcon,
} from '@mui/icons-material';
import { useTermsAndConditions } from '../hooks/useTermsAndConditions';
import { usePlatformDocument } from '../hooks/usePlatformDocument';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import { isSafeAttachmentUrl } from '../utils/isSafeAttachmentUrl';

interface UserProfileMenuProps {
  unreadNotifications?: number;
}

const UserProfileMenu: React.FC<UserProfileMenuProps> = ({ unreadNotifications = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { url: termsUrl } = useTermsAndConditions();
  const { url: privacyPolicyUrl } = usePlatformDocument('privacy-policy.pdf');
  const { url: websiteTermsUrl } = usePlatformDocument('website-terms.pdf');
  const { url: platformFeesUrl } = usePlatformDocument('platform-fees.pdf');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = async () => {
    // Prevent multiple logout attempts
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    try {
      // Do not block UX on network sign-out; logout clears UI state immediately.
      handleClose();
      navigate('/');

      void logout().catch((error) => {
        console.error('Logout error:', error);
        enqueueSnackbar('Logout had an issue, but you have been signed out locally.', {
          variant: 'warning',
        });
      });
    } catch (error) {
      console.error('Logout error:', error);
      enqueueSnackbar('Logout failed. Please try again.', {
        variant: 'error'
      });
      handleClose();
      // Still navigate away for security
      navigate('/');
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (!user) return null;


  // Get user initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <Tooltip title="Account settings">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 1 }}
          aria-label="Account settings"
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Badge badgeContent={unreadNotifications} color="error">
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem'
              }}
            >
              {getInitials(user.name || user.email || 'U')}
            </Avatar>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 280,
            mt: 1.5,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        MenuListProps={{
          sx: {
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box component="li" sx={{ px: 2, py: 1.5, pb: 1, listStyle: 'none' }}>
          <Typography variant="subtitle2" fontWeight="bold" noWrap>
            {user.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user.email}
          </Typography>
        </Box>

        <Divider component="li" />

        {/* Notifications Section */}
        <MenuItem
          onClick={() => handleNavigate('/notifications')}
          selected={location.pathname === '/notifications'}
        >
          <ListItemIcon>
            <Badge badgeContent={unreadNotifications} color="error">
              <NotificationsIcon fontSize="small" />
            </Badge>
          </ListItemIcon>
          <ListItemText>Notifications</ListItemText>
        </MenuItem>

        <Divider component="li" sx={{ my: 1 }} />

        {/* Settings Menu Item */}
        <MenuItem
          onClick={() => handleNavigate('/settings/profile')}
          selected={location.pathname.startsWith('/settings/')}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Account & Settings</ListItemText>
        </MenuItem>

        {/* About Us — public page, reachable while logged in (card bcbd1b68) */}
        <MenuItem
          component={RouterLink}
          to="/about-us"
          selected={location.pathname === '/about-us'}
        >
          <ListItemIcon>
            <AboutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>About Us</ListItemText>
        </MenuItem>

        <Divider component="li" sx={{ my: 1 }} />

        {/* Agreements Section */}
        <ListSubheader component="li" disableSticky sx={{ px: 2, py: 0.5, lineHeight: 'normal', bgcolor: 'transparent', listStyle: 'none' }}>
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            AGREEMENTS
          </Typography>
        </ListSubheader>

        <MenuItem
          onClick={() => { if (termsUrl && isSafeAttachmentUrl(termsUrl)) window.open(termsUrl, '_blank', 'noopener,noreferrer'); }}
        >
          <ListItemIcon>
            <GavelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Master Agreement</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => { if (privacyPolicyUrl && isSafeAttachmentUrl(privacyPolicyUrl)) window.open(privacyPolicyUrl, '_blank', 'noopener,noreferrer'); }}
        >
          <ListItemIcon>
            <GavelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Privacy Policy</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => { if (websiteTermsUrl && isSafeAttachmentUrl(websiteTermsUrl)) window.open(websiteTermsUrl, '_blank', 'noopener,noreferrer'); }}
        >
          <ListItemIcon>
            <GavelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Website Terms</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => { if (platformFeesUrl && isSafeAttachmentUrl(platformFeesUrl)) window.open(platformFeesUrl, '_blank', 'noopener,noreferrer'); }}
        >
          <ListItemIcon>
            <GavelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Platform Fees</ListItemText>
        </MenuItem>


        {/* Logout */}
        <MenuItem
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          <ListItemIcon>
            {isLoggingOut ? (
              <CircularProgress size={20} />
            ) : (
              <LogoutIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserProfileMenu;
